import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../AppContext';
import ForgotPasswordPanel from './auth/ForgotPasswordPanel';

export default function LoginModal() {
  const { loginOpen, closeLogin, login } = useApp();
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [shaking, setShaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (loginOpen) {
      setMode('login');
      setPassword('');
      setError(null);
      setShaking(false);
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [loginOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && loginOpen) closeLogin();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [loginOpen, closeLogin]);

  const verify = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      await login(username.trim(), password);
    } catch (e: any) {
      setError(e?.status === 401 ? 'Credenziali non corrette. Riprova.' : (e?.message || 'Errore di accesso.'));
      setPassword('');
      setShaking(false);
      setTimeout(() => setShaking(true), 0);
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  if (!loginOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-6">
      <div className="absolute inset-0 bg-forestDark/70 backdrop-blur-sm" onClick={closeLogin} />

      <div className={`login-card-anim relative bg-cream rounded-3xl border border-forest/10 shadow-2xl w-full max-w-md p-8 md:p-10 space-y-6 ${shaking ? 'login-shake' : ''}`}>
        <button onClick={closeLogin} className="absolute top-4 right-5 text-charcoal/40 hover:text-forest text-2xl leading-none smooth-transition">×</button>

        {mode === 'login' ? (
          <>
            <div className="space-y-3 text-center">
              <div className="w-14 h-14 rounded-2xl bg-sage/10 flex items-center justify-center text-forest mx-auto">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              </div>
              <span className="text-xs uppercase tracking-[0.2em] text-sage font-bold">Portale Clinico</span>
              <h3 className="font-serif text-2xl text-forestDark">Accesso Area Riservata</h3>
              <p className="text-xs text-charcoal/60">Inserisci le credenziali per accedere al pannello di controllo del Dr. Balducci.</p>
            </div>

            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-charcoal/70">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') verify(); }}
                  placeholder="admin"
                  autoComplete="username"
                  className="w-full bg-beigeBG border border-forest/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 smooth-transition"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-charcoal/70">Password</label>
                <input
                  ref={inputRef}
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') verify(); }}
                  placeholder="••••••••••"
                  autoComplete="current-password"
                  className="w-full bg-beigeBG border border-forest/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 smooth-transition"
                />
                {error && <p className="text-xs text-rose-700 font-medium pt-1">{error}</p>}
              </div>
            </div>

            <button
              onClick={verify}
              disabled={loading}
              className="btn-shine w-full bg-forest hover:bg-forestDark text-cream py-3 rounded-full text-sm font-semibold smooth-transition hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {loading ? 'Accesso in corso…' : 'Accedi'}
            </button>

            <div className="text-center">
              <button
                onClick={() => { setMode('forgot'); setError(null); }}
                className="text-xs uppercase tracking-widest font-bold text-sage hover:text-forest smooth-transition"
              >
                Password dimenticata?
              </button>
            </div>
          </>
        ) : (
          <ForgotPasswordPanel onBack={() => setMode('login')} />
        )}
      </div>
    </div>
  );
}
